import React, { useEffect, useRef, useState } from "react";
import { useLang } from "./LanguageProvider";
import { projectsData } from "@/i18n/translations";
import { Plus } from "lucide-react";
import Lightbox from "./Lightbox";

const PATTERN = [
  "md:col-span-5 md:row-span-2",
  "md:col-span-4",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-4",
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-6",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-6",
  "md:col-span-3",
  "md:col-span-3",
];

export default function Projects() {
  const { t, lang, isRtl } = useLang();
  const [showAll, setShowAll] = useState(false);
  const [lbIdx, setLbIdx] = useState(null);
  const ninthRef = useRef(null);
  const visible = showAll ? projectsData : projectsData.slice(0, 9);

  const items = projectsData.map((p, i) => ({
    src: p.img,
    caption: lang === "ar" ? p.name_ar : p.name_en,
    subcaption: isRtl
      ? `مشروع ${String(i + 1).padStart(2, "0")} · أنابيب FlowWhite CPVC`
      : `Project ${String(i + 1).padStart(2, "0")} · FlowWhite CPVC installed`,
  }));

  // When showAll flips to true, briefly scroll to the 10th tile so user sees the reveal
  useEffect(() => {
    if (showAll && ninthRef.current) {
      const t = setTimeout(() => {
        ninthRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 60);
      return () => clearTimeout(t);
    }
  }, [showAll]);

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative bg-brand-navyDeep py-24 md:py-40"
    >
      <div className="container mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 md:mb-20">
          <div className="lg:col-span-5 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-brand-teal" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-brand-teal font-semibold">
                {t.projects.overline}
              </span>
            </div>
            <h2 className="font-heading text-white text-3xl sm:text-4xl md:text-5xl lg:text-[56px] leading-[1.05] font-medium tracking-tight">
              {t.projects.title}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 reveal reveal-delay-1">
            <p className={`text-base md:text-lg text-white/65 leading-relaxed ${isRtl ? "text-right" : "text-left"}`}>
              {t.projects.lead}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-white/40">
              <span>{String(projectsData.length).padStart(2, "0")} projects</span>
              <span className="w-px h-3 bg-white/20" />
              <span>2018 to present</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-5 auto-rows-[180px] md:auto-rows-[230px]">
          {visible.map((p, i) => (
            <button
              key={p.img + i}
              ref={i === 9 ? ninthRef : null}
              type="button"
              onClick={() => setLbIdx(i)}
              data-testid={`project-tile-${i}`}
              className={`group relative overflow-hidden rounded-xl ${PATTERN[i % PATTERN.length] || "md:col-span-4"} bg-brand-surface text-left animate-[fade-up_0.6s_ease-out_forwards] opacity-0`}
              style={{ animationDelay: `${(i % 5) * 60}ms` }}
            >
              <img
                src={p.img}
                alt={lang === "ar" ? p.name_ar : p.name_en}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform [transition-duration:1400ms] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navyDarker/95 via-brand-navyDeep/35 to-transparent" />
              <div className={`absolute inset-x-0 bottom-0 p-5 md:p-6 flex items-end justify-between gap-3`}>
                <div className={isRtl ? "text-right" : "text-left"}>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-brand-teal font-semibold">
                    {isRtl ? `مشروع ${String(i + 1).padStart(2, "0")}` : `Project ${String(i + 1).padStart(2, "0")}`}
                  </span>
                  <h3 className="mt-1.5 text-white text-base md:text-lg font-medium leading-tight">
                    {lang === "ar" ? p.name_ar : p.name_en}
                  </h3>
                </div>
                <span className="opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/30 bg-white/[0.06] text-white flex-shrink-0">
                  <Plus size={14} />
                </span>
              </div>
            </button>
          ))}
        </div>

        {projectsData.length > 9 && (
          <div className="mt-12 flex justify-center">
            <button
              data-testid="projects-toggle-btn"
              onClick={() => setShowAll((v) => !v)}
              className="inline-flex items-center gap-2 text-sm font-medium text-white border border-white/20 hover:border-white/60 hover:bg-white/[0.04] rounded-full px-7 py-3 transition-all"
            >
              {showAll
                ? lang === "ar" ? "إظهار أقل" : "Show less"
                : lang === "ar" ? `عرض الكل (${projectsData.length})` : `Show all ${projectsData.length} projects`}
            </button>
          </div>
        )}
      </div>

      <Lightbox
        items={items}
        index={lbIdx}
        onClose={() => setLbIdx(null)}
        onPrev={() => setLbIdx((i) => (i - 1 + items.length) % items.length)}
        onNext={() => setLbIdx((i) => (i + 1) % items.length)}
      />
    </section>
  );
}
